import { useEffect, useState } from 'react';
import Button from '../../components/button/Button';
import LinkButton from '../../components/button/LinkButton';
import InputField from '../../components/inputField/InputField';
import ScreenContainer from '../../components/layout/ScreenContainer';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { SignupAPI } from '../../logic/signupApi/signupApi';
import Captcha from '../../components/captcha/Captcha';
import ErrorMessage from '../../components/errorMessage/ErrorMessage';
import Modal from '../../components/modal/Modal';
import ScrollBox from '../../components/scrollBox/ScrollBox';
import Checkbox from '../../components/checkbox/Checkbox';
import Loader from '../../components/loader/Loader';
import { useCountry, formatPhoneNumber } from '../../utils/phoneNumberUtils';
import platform from 'platform';
import { useTranslation } from 'react-i18next';
import { trackUserRegistered } from '../../utils/posthogEvents';

type CodeTypes = 'community_link' | 'community_typed' | 'user_link' | 'user_typed' | null;

interface RegistrationScreenProps {
  communityName: string | null;
  setCommunityName: React.Dispatch<React.SetStateAction<string | null>>;
  setCommunityLogo: React.Dispatch<React.SetStateAction<string | null>>;
}

const RegistrationScreen = ({ communityName, setCommunityName, setCommunityLogo }: RegistrationScreenProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors, defaultValues }, watch } = useForm();

  const [refresh, doRefresh] = useState(0);

  const [phoneNumber, setPhoneNumber] = useState('');

  const [linkRef, setLinkRef] = useState<string | null>(null);

  const [captcha, setCaptcha] = useState<string | null>(null);

  const [linkedCode, setLinkedCode] = useState<string | null>(null);

  const [cTypeState, setCType] = useState<CodeTypes>(null);
  const [associationToCommunityState, setAssociationToCommunity] = useState<string>('Member does not claim to belong to community');
  const [isWrongCodeState, setIsWrongCode] = useState<boolean>(false);

  const [proceedWithError, setProceedWithError] = useState<boolean>(false);

  const [errMsgVisible, setErrMsgVisible] = useState(false);
  const [errorMode, setErrorMode] = useState<'warning' | 'error'>('error');
  const [errMsgTxt, setErrMsgTxt] = useState('');

  const [isWaiverModalOpened, setIsWaiverModalOpened] = useState(false);
  const [isWaiverAccepted, setIsWaiverAccepted] = useState(false);

  const email = useLocation().state?.email || null;
  const phone = useLocation().state?.phone || null;

  const [isLoading, setIsLoading] = useState(false);

  const referralCode = watch('referral');

  const { country } = useCountry();

  useEffect(() => {
    checkReferralId();
  }, []);

  useEffect(() => {
    setPhoneNumber(phone);
  }, [phone]);

  useEffect(() => {
    if (isWrongCodeState) {
      setProceedWithError(false);
      setErrMsgVisible(false);
      setIsWrongCode(false);
      setLinkedCode(null);
      if (referralCode === '') {
        setAssociationToCommunity('Member does not claim to belong to community');
      }
    }
  }, [referralCode]);

  //TODO: uncomment when we know what to do with this tooltip
  // useEffect(()=>{
  //   if(timer<tooltipAppearTime){
  //     setTimeout(()=>{
  //       setTimer(timer+1);
  //     }, 1000);
  //   }
  //   if(timer===tooltipAppearTime){
  //     setIsTooltipVisible(true);
  //   }
  // },[timer]);

  const checkReferralId = async () => {
    let url = new URL(window.location.href);

    const code = url.searchParams.get('ref');

    if (code) {
      setLinkRef(code);
      assignCode(atob(code));
    }
  }

  const assignCode = async (code: string, isLink: boolean = true) => {

    return await SignupAPI.checkIfInviteCodeIsValid(code, isLink).then((result) => {
      console.log("Refereal community:", result);

      const associationToCommunity = `Member with an invite code for: ${result.communityName}, ${code}`;

      setLinkedCode(code);
      setCommunityName(result.communityName);
      setCommunityLogo(result.communityLogo)
      setCType(result.cType);
      setIsWrongCode(false);
      setAssociationToCommunity(associationToCommunity);
      setErrMsgVisible(false);

      if (isLink) {
        return;
      }

      const cType = result.cType;
      return { cType, associationToCommunity, isWrongCode: false };

    }).catch((error: { message: string, code: 'community_forbidden' | 'code_invalid', communityName: string | null, defaultCommunityName: string }) => {
      console.log('error while checking invite code: ', error);

      let associationToCommunity = `Member tried using non-existing referral code: ${code}`;

      setErrorMode('warning');

      switch (error.code) {
        // we don't care about that case but I'm leaving it just in case...
        case 'community_forbidden':
          setErrMsgVisible(true);
          setErrMsgTxt(t('COMMUNITY_DOES_NOT_ALLOW_INVITES', { communityName: error.communityName, defaultCommunityName: error.defaultCommunityName }));
          associationToCommunity = `Member tried using community forbidden referral code ${code}`
          break;
        case 'code_invalid':
          setErrMsgVisible(true);
          setErrMsgTxt(t('INVALID_CODE_REFERRED', { code, defaultCommunityName: error.defaultCommunityName }));
          break;
        default:
          setErrMsgVisible(true);
          setErrMsgTxt(t('REFERRAL_CODE_ERROR'));
          break;
      }

      setAssociationToCommunity(associationToCommunity);
      setIsWrongCode(true);
      setProceedWithError(true);
      setLinkedCode(code);

      if (isLink) {
        return;
      }

      return { cType: null, associationToCommunity, isWrongCode: true }

    });
  }

  const onSubmit = async (data: any) => {

    setIsLoading(true);

    let phone = '';
    if (phoneNumber) {
      // Format phone number (ensures single + prefix)
      phone = formatPhoneNumber(phoneNumber);
    }

    let email = data.email.trim();
    let fullName = data.fullName.trim();
    let typedCode = data.referral?.trim();

    let associationToCommunity = associationToCommunityState;
    let cType = cTypeState;
    let isWrongCode = isWrongCodeState;

    if (typedCode) {
      const params = await assignCode(typedCode, false);
      if (params) {
        associationToCommunity = params.associationToCommunity;
        cType = params.cType;
        isWrongCode = params.isWrongCode;
      }
    }

    if (isWrongCode && !proceedWithError) {
      setIsLoading(false);
      return;
    }

    const deviceInfo = `${platform.name} ${platform.version} on ${platform.os?.family} ${platform.os?.version ? platform.os?.version + ' ' : ''}${platform.os?.architecture}`;

    await SignupAPI.validateAndRegisterParticipant({
      recaptchaToken: captcha,
      fullName: fullName,
      email: email,
      telephoneMobile: phone,
      inviteCode: typedCode || linkedCode || null,
      cType: cType,
      associationToCommunity: associationToCommunity,
      requestURL: window.location.hostname,
      registrationType: 'direct',
      deviceInfo: deviceInfo,
      language: i18n.language,
      // reference: If they want to have a text "How did you heard about Televeda?" or something..."
    }).then(async () => {
      trackUserRegistered(email);
      return await SignupAPI.sendLoginCode({
        whereToSendTheCode: 'email',
        email: email,
        language: i18n.language
      }).catch((error: unknown) => {
        setErrMsgTxt(t('COULD_NOT_SEND_VERIFICATION_CODE'));
        setErrorMode('error');
        setErrMsgVisible(true);
        console.log('error while sending login code: ', error)
      })
    }).then((response: any) => {
      if (response.result === 'ok') {
        navigate("/login/verification", { state: { email: email, phone } });
      }
    }).catch((error: any) => {
      console.log('error', error);

      if (error.opCode) {

        switch (error.opCode) {
          case 'reg/email-taken':
            console.log("Navigate to login with email: ", email);
            navigate(`/login${linkRef ? `?ref=${linkRef}` : ''}`, { state: { email: email, phone } });
            break;

          case 'reg/phone-taken':
            console.log("Navigate to login with phone: ", phone);
            // Format phone number (ensures single + prefix)
            const formattedPhoneForLogin = formatPhoneNumber(phoneNumber);
            navigate(`/login/phone${linkRef ? `?ref=${linkRef}` : ''}`, { state: { email: email, phone: formattedPhoneForLogin } });
            break;

          case 'reg/invalid-name':
            setErrMsgTxt(t('INVALID_NAME_LENGTH'));
            setErrorMode('error');
            setErrMsgVisible(true);
            break;

          default:
            let message = error.message;

            switch (message) {
              case 'TOO_LONG':
                message = t('PHONE_TOO_LONG');
                break;
              case 'TOO_SHORT':
                message = t('PHONE_TOO_SHORT');
                break;
              case 'INVALID_LENGTH':
              case 'Invalid format.':
                message = t('PHONE_INVALID_FORMAT');
                break;
              default:
                break;
            }

            setErrMsgTxt(message);
            setErrorMode('error');
            setErrMsgVisible(true);
        }
      }
      else {
        setErrMsgTxt(error.message);
        setErrorMode('error');
        setErrMsgVisible(true);
      }

      setIsLoading(false);
      doRefresh(prev => prev + 1);
    });
  };

  const captchaOnChange = (value: string | null) => {
    setCaptcha(value);
  }

  return (
    <ScreenContainer pageTitle={t('PAGE_TITLE_SIGNUP')}>
      {
        errMsgVisible && <ErrorMessage message={errMsgTxt} onClose={() => { setErrMsgVisible(false); }} mode={errorMode} />
      }

      <h2 className='size-h2 u-mb-2 u-text-bold'>{t('NEW_MEMBER_REGISTRATION')}</h2>
      <Modal
        isOpen={isWaiverModalOpened}
        onClose={() => setIsWaiverModalOpened(false)}
        title={t('WAIVER_OF_LIABILITY')}
        size='md'
      >
        <div className='row u-mb-2'>
          <div className='col'>
            <p className='large'>{t('WAIVER_TEXT_FIRST')}</p>
          </div>
        </div>
        <ScrollBox children={
          <>
            <span>{t('WAIVER_TEXT_SECOND')}
              <span className='u-text-bold'>{t('WAIVER_TEXT_THIRD')}</span>
            </span>
            &nbsp;
            <span className='u-text-bold'>
              {t('WAIVER_TEXT_FOURTH')}
            </span>
            &nbsp;
            <span>
              {t('WAIVER_TEXT_FIFTH')}
            </span>
            &nbsp;
            <span className='u-text-bold'>
              {t('WAIVER_TEXT_SIXTH')}
            </span>
          </>}
        />
        <div className='row u-mt-2'>
          <div className='col'>
            <Button title={t('CONTINUE')} onClick={() => {
              setIsWaiverModalOpened(false)
              setIsWaiverAccepted(true);
            }} />
          </div>
        </div>
      </Modal>
      <form onSubmit={handleSubmit(onSubmit)}>
        {
          communityName &&
          <span style={{ marginTop: 15, marginBottom: 15 }}>
            <b> {t('REGISTRATION_SCREEN_TEXT_SECOND')}</b> <span>{communityName}</span>.
          </span>
        }

        <InputField
          name="fullName"
          errors={errors}
          register={register}
          options={{
            required: true,
            pattern: {
              value: /^\s*[A-Za-z/./]+(\s+[A-Za-z/./]+)*\s*$/,
              message: t('INVALID_FULL_NAME')
            },
            validate: {
              minLength: (v) => v.length >= 0,
            }
          }}
          placeholder={`${t('FULL_NAME')} *`}
        />
        <InputField
          name="email"
          errors={errors}
          register={register}
          options={{
            required: true,
            pattern: {
              value: /^\s*[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\s*$/i,
              message: t('INVALID_EMAIL')
            },
            validate: {
              minLength: (v) => v.length >= 0,
            }
          }}
          defaultValue={email}
          placeholder={`${t('EMAIL_ADDRESS')} *`}
          aria-label='email'
        />
        {/* <PhoneInput country={country ? country : ''} value={phoneNumber} onChange={(phone)=> setPhoneNumber(phone)}/> */}
        <div className='form-group u-mb-2'>
          {
            // If country is assigned and there is a phone value in the field the value is removed for some reason
            phone ?
              // if we come to this screen from the registration with a phone number,
              // the country is automatically detected from the phone number,
              // so we can bypass the bug by not fetching the country
              <PhoneInput
                value={phoneNumber}
                inputClass={'phone-input'}
                buttonClass={'phone-button'}
                onChange={(phone) => {
                  setPhoneNumber(phone)
                }} />
              :
              // if we come here without a phone number to display,
              // we fetch and assign the country
              // and since there is no value that is going to disappear its okay
              <PhoneInput
                country={country}
                inputClass={'phone-input'}
                buttonClass={'phone-button'}
                value={phoneNumber}
                onChange={(phone) => {
                  setPhoneNumber(phone)
                }} />
          }
        </div>
        {!communityName && <InputField
          name="referral"
          errors={errors}
          register={register}
          placeholder={t('REFERAL_CODE_OPTIONAL')} isOptional={true}
        />}
        <Checkbox
          name='acceptWaiver'
          register={register}
          errors={errors}
          options={{ required: true }}
          value={isWaiverAccepted}
          onChange={() => setIsWaiverAccepted(!isWaiverAccepted)}
        >
          {/*
                  Deliberate deviation from pages/login.html: there the text carries both
                  `for="termsCheck"` and the modal trigger, so clicking it ticks the box AND
                  opens the waiver. Keeping only the modal trigger preserves this app's original
                  flow — the box gets ticked by accepting inside the waiver — so the user can't
                  end up consenting without the waiver having been shown. It's a real button
                  (not the original span) so it's keyboard-reachable.
                */}
          <button
            type='button'
            className='btn btn-clean u-ml-1 u-color-primary u-hover-color-primary-light'
            onClick={() => setIsWaiverModalOpened(true)}
          >{t('REGISTRATION_TEXT_SIXTH')}</button>
        </Checkbox>
        {isLoading && <Loader />}
        <Button disabled={isLoading} title={isWrongCodeState ? t('CONTINUE_ANYWAY') : t('CONTINUE')} />
        <div className='u-mt-2'>
          <Captcha onChange={captchaOnChange} refresh={refresh} />
        </div>
      </form>
      { !email && !phone &&
        <LinkButton
          block
          title={t('LOGIN_INSTEAD')}
          onClick={() => navigate(`/login${linkRef ? `?ref=${linkRef}` : ''}`)}
        />
      }
      {
        email && <LinkButton
          block
          title={t('EMAIL_SCREEN_TEXT_FIRST')}
          onClick={() => navigate(`/login/phone${linkRef ? `?ref=${linkRef}` : ''}`, { state: { email: email, phone } })}
        />
      }
      {
        phone && <LinkButton
          block
          title={t('PHOHE_SCREEN_TEXT_THIRD')}
          onClick={() => navigate(`/login/${linkRef ? `?ref=${linkRef}` : ''}`, { state: { email: email, phone: phoneNumber } })}
        />
      }
    </ScreenContainer>
  )
};

export default RegistrationScreen